// LeadFormsView — admin UI for public lead-capture forms.
//
// Three states:
//   list     → grid of forms with views/submissions counters + actions
//   editor   → create or edit a form (title, fields, branding, thank-you)
//   detail   → form detail with link/QR + submissions feed
//
// Public landing for the resulting form lives at /form/:slug (PublicFormPage).

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "./Toast.jsx";
import {
  Plus, Edit2, Trash2, Copy, ExternalLink, Eye, Send, X, Check,
  GripVertical, ChevronDown, FileText, QrCode, AlertCircle, RefreshCw,
} from "lucide-react";
import {
  listForms, createForm, updateForm, deleteForm,
  getForm, listFormSubmissions,
} from "../api.js";
import { confirmDialog } from "./ui.jsx";
import { copyText } from "../utils.js";

const FIELD_TYPES = [
  { value: "text",     label: "Texto corto" },
  { value: "textarea", label: "Texto largo" },
  { value: "email",    label: "Email" },
  { value: "phone",    label: "Teléfono" },
  { value: "number",   label: "Número" },
  { value: "date",     label: "Fecha" },
  { value: "select",   label: "Lista desplegable" },
  { value: "radio",    label: "Opción única (radio)" },
  { value: "checkbox", label: "Casillas (checkboxes)" },
];

const POLICY_INTEREST = [
  "HEALTH", "LIFE", "AUTO", "HOME", "RENTERS", "MEDICARE_SUPPLEMENT",
  "MEDICARE_ADVANTAGE", "DENTAL", "VISION", "DISABILITY", "ANNUITY",
  "COMMERCIAL", "WORKERS_COMP",
];

const DEFAULT_FIELDS = [
  { id: "firstName", label: "First name",    type: "text",  required: true },
  { id: "lastName",  label: "Last name",     type: "text",  required: true },
  { id: "email",     label: "Email",         type: "email", required: true },
  { id: "phone",     label: "Phone",         type: "phone", required: false },
];

function uid() {
  return "fld_" + Math.random().toString(36).slice(2, 10);
}

function publicUrlFor(slug) {
  if (typeof window === "undefined") return `/form/${slug}`;
  return `${window.location.origin}/form/${slug}`;
}

export function LeadFormsView({ currentUser, lang = "en" }) {
  const es = lang === "es";
  const t = (esTxt, enTxt) => (es ? esTxt : enTxt);

  const [view, setView] = useState("list"); // list | editor | detail
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // form being edited (or null = new)
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setForms(await listForms());
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Sidebar reset
  useEffect(() => {
    const h = (e) => {
      if (e?.detail?.view === "lead_forms") {
        setView("list");
        setEditing(null);
        setOpenId(null);
      }
    };
    window.addEventListener("deskte:nav-reset", h);
    return () => window.removeEventListener("deskte:nav-reset", h);
  }, []);

  if (view === "editor") {
    return (
      <FormEditor
        initial={editing}
        lang={lang}
        onCancel={() => { setView("list"); setEditing(null); }}
        onSaved={async () => { await refresh(); setView("list"); setEditing(null); }}
      />
    );
  }
  if (view === "detail" && openId) {
    return (
      <FormDetail
        formId={openId}
        lang={lang}
        onBack={() => { setView("list"); setOpenId(null); }}
        onEdit={(form) => { setEditing(form); setView("editor"); }}
        onDeleted={async () => { await refresh(); setView("list"); setOpenId(null); }}
      />
    );
  }

  return (
    <div style={{ padding: "1.5rem", maxWidth: 1100, margin: "0 auto" }}>
      <Header lang={lang} onCreate={() => { setEditing(null); setView("editor"); }} />

      {error && (
        <div style={{
          background: "#3a1a1a", border: "1px solid #ef4444",
          borderRadius: 10, padding: ".75rem 1rem", marginBottom: ".75rem",
          color: "#fca5a5", display: "flex", alignItems: "center", gap: 10, fontSize: ".88rem",
        }}>
          <AlertCircle size={16} /><span>{error}</span>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "var(--c-text2,#7aa3c0)" }}>
          {t("Cargando formularios…", "Loading forms…")}
        </div>
      ) : forms.length === 0 ? (
        <EmptyState lang={lang} onCreate={() => { setEditing(null); setView("editor"); }} />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: "1rem" }}>
          {forms.map((f) => (
            <FormCard
              key={f.id}
              form={f}
              lang={lang}
              onOpen={() => { setOpenId(f.id); setView("detail"); }}
              onEdit={() => { setEditing(f); setView("editor"); }}
              onDelete={async () => {
                if (!(await confirmDialog({ message: t(`¿Eliminar el formulario "${f.title}"? Las submissions previas no se borran.`, `Delete form "${f.title}"? Past submissions are kept.`), danger: true }))) return;
                try { await deleteForm(f.id); await refresh(); } catch (err) { setError(err.message); }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Header + EmptyState + Card
// ----------------------------------------------------------------------

function Header({ lang, onCreate }) {
  const t = (es, en) => (lang === "es" ? es : en);
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem", flexWrap: "wrap", gap: ".75rem" }}>
      <div>
        <h2 style={{ margin: 0, fontSize: "1.4rem", color: "var(--c-white,#eef6ff)" }}>
          📋 {t("Formularios públicos", "Public forms")}
        </h2>
        <div style={{ fontSize: ".82rem", color: "var(--c-text2,#7aa3c0)", marginTop: 2 }}>
          {t("Crea formularios para compartir en WhatsApp, Instagram o tu web. Cada submission se vuelve un Lead automáticamente.",
             "Create forms to share on WhatsApp, Instagram or your site. Every submission becomes a Lead automatically.")}
        </div>
      </div>
      <button
        onClick={onCreate}
        style={{
          padding: ".7rem 1.25rem", borderRadius: 10,
          background: "linear-gradient(135deg, #00d4ff, #00a8c8)",
          color: "#0a1628", border: "none", cursor: "pointer",
          fontWeight: 700, fontSize: ".88rem",
          display: "inline-flex", alignItems: "center", gap: 6,
        }}
      >
        <Plus size={16} /> {t("Nuevo formulario", "New form")}
      </button>
    </div>
  );
}

function EmptyState({ lang, onCreate }) {
  const t = (es, en) => (lang === "es" ? es : en);
  return (
    <div style={{
      textAlign: "center", padding: "3rem 1rem",
      background: "var(--c-surface,#0d1c2e)", border: "1px dashed var(--c-border,#1a3550)",
      borderRadius: 16,
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: 16, margin: "0 auto 1rem",
        background: "linear-gradient(135deg, #00d4ff22, #00d4ff08)",
        border: "1px solid #00d4ff44", display: "grid", placeItems: "center",
      }}>
        <FileText size={28} color="#00d4ff" />
      </div>
      <h3 style={{ margin: "0 0 .5rem", color: "var(--c-white,#eef6ff)", fontSize: "1.1rem" }}>
        {t("Aún no tienes formularios", "No forms yet")}
      </h3>
      <p style={{ color: "var(--c-text2,#7aa3c0)", fontSize: ".88rem", margin: "0 0 1.5rem", maxWidth: 420, marginInline: "auto", lineHeight: 1.55 }}>
        {t("Crea uno como 'Cotizar mi seguro de salud' y comparte el link. Cada quien que lo llene entra a tu CRM como Lead.",
           "Create one like 'Quote my health insurance' and share the link. Anyone who fills it enters your CRM as a Lead.")}
      </p>
      <button onClick={onCreate} style={{
        padding: ".7rem 1.5rem", borderRadius: 10,
        background: "linear-gradient(135deg, #00d4ff, #00a8c8)", color: "#0a1628",
        border: "none", cursor: "pointer", fontWeight: 700, fontSize: ".88rem",
        display: "inline-flex", alignItems: "center", gap: 6,
      }}>
        <Plus size={16} /> {t("Crear el primero", "Create the first one")}
      </button>
    </div>
  );
}

function FormCard({ form, lang, onOpen, onEdit, onDelete }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const url = publicUrlFor(form.slug);
  return (
    <div style={{
      background: "var(--c-surface,#0d1c2e)",
      border: "1px solid var(--c-border,#1a3550)",
      borderRadius: 14, padding: "1.1rem",
      display: "flex", flexDirection: "column", gap: ".7rem",
      transition: "border-color .15s, transform .1s",
      cursor: "pointer",
    }}
    onClick={onOpen}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#00d4ff66"; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--c-border,#1a3550)"; }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "1.02rem", fontWeight: 700, color: "var(--c-white,#eef6ff)", marginBottom: 4 }}>
            {form.title}
          </div>
          <div style={{
            fontSize: ".72rem", color: "var(--c-text3,#45688a)",
            fontFamily: "var(--font-mono,monospace)", wordBreak: "break-all",
          }}>
            /form/{form.slug}
          </div>
        </div>
        <div style={{
          fontSize: ".68rem", color: form.isActive ? "#10b981" : "#94a3b8",
          background: form.isActive ? "#10b98115" : "#94a3b815",
          border: `1px solid ${form.isActive ? "#10b98144" : "#94a3b844"}`,
          padding: "3px 9px", borderRadius: 999, fontWeight: 700, letterSpacing: ".04em",
          flexShrink: 0,
        }}>
          {form.isActive ? "ACTIVE" : "OFF"}
        </div>
      </div>

      {form.description && (
        <div style={{ fontSize: ".82rem", color: "var(--c-text2,#7aa3c0)", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {form.description}
        </div>
      )}

      <div style={{ display: "flex", gap: "1rem", fontSize: ".78rem", color: "var(--c-text2,#7aa3c0)" }}>
        <div><Eye size={12} style={{ verticalAlign: "-2px" }} /> {form.viewCount} {t("vistas", "views")}</div>
        <div><Send size={12} style={{ verticalAlign: "-2px" }} /> {form._count?.submissions ?? form.submissionCount ?? 0} {t("envíos", "submissions")}</div>
      </div>

      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
        <button
          onClick={(e) => { e.stopPropagation(); copyText(url).then((ok) => { if (ok) toast.success(t("¡Copiado!", "Copied!")); else toast.error(t("No se pudo copiar", "Couldn't copy")); }); }}
          title={t("Copiar link público", "Copy public link")}
          style={btn("primary")}
        >
          <Copy size={12} /> {t("Copiar link", "Copy link")}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); window.open(url, "_blank", "noopener,noreferrer"); }}
          title={t("Abrir form público", "Open public form")}
          style={btn("ghost")}
        >
          <ExternalLink size={12} />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onEdit(); }} title={t("Editar", "Edit")} style={btn("ghost")}>
          <Edit2 size={12} />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} title={t("Eliminar", "Delete")} style={btn("danger")}>
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

function btn(kind) {
  const map = {
    primary: { bg: "#00d4ff22", color: "#00d4ff", border: "#00d4ff66" },
    ghost:   { bg: "var(--c-surface2,#112538)", color: "var(--c-text2,#7aa3c0)", border: "var(--c-border,#1a3550)" },
    danger:  { bg: "#7f1d1d22", color: "#fca5a5", border: "#ef444466" },
  }[kind];
  return {
    background: map.bg, color: map.color, border: `1px solid ${map.border}`,
    padding: ".4rem .65rem", borderRadius: 8, cursor: "pointer", fontSize: ".75rem",
    display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 600,
  };
}

// ----------------------------------------------------------------------
// FormEditor — create / edit
// ----------------------------------------------------------------------

function FormEditor({ initial, onCancel, onSaved, lang }) {
  const es = lang === "es";
  const t = (esTxt, enTxt) => (es ? esTxt : enTxt);

  const [title, setTitle] = useState(initial?.title || "");
  const [slug, setSlug] = useState(initial?.slug || "");
  const [slugTouched, setSlugTouched] = useState(!!initial?.slug);
  const [description, setDescription] = useState(initial?.description || "");
  const [interestType, setInterestType] = useState(initial?.interestType || "");
  const [thankYouTitle, setThankYouTitle] = useState(initial?.thankYouTitle || "");
  const [thankYouMessage, setThankYouMessage] = useState(initial?.thankYouMessage || "");
  const [redirectUrl, setRedirectUrl] = useState(initial?.redirectUrl || "");
  const [brandColor, setBrandColor] = useState(initial?.brandColor || "#00d4ff");
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [fields, setFields] = useState(() => {
    const init = Array.isArray(initial?.fields) ? initial.fields : null;
    return (init && init.length > 0)
      ? init.map((f) => ({ ...f, _localId: uid() }))
      : DEFAULT_FIELDS.map((f) => ({ ...f, _localId: uid() }));
  });
  const [previewMode, setPreviewMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Auto-slug from title until user touches it manually
  useEffect(() => {
    if (slugTouched) return;
    const s = title
      .toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
    setSlug(s || "form");
  }, [title, slugTouched]);

  function addField(type = "text") {
    setFields((arr) => [...arr, { _localId: uid(), id: `field_${arr.length}`, label: "Untitled", type, required: false }]);
  }
  function updateField(localId, patch) {
    setFields((arr) => arr.map((f) => (f._localId === localId ? { ...f, ...patch } : f)));
  }
  function removeField(localId) {
    setFields((arr) => arr.filter((f) => f._localId !== localId));
  }
  function moveField(localId, dir) {
    setFields((arr) => {
      const i = arr.findIndex((f) => f._localId === localId);
      if (i < 0) return arr;
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const out = arr.slice();
      [out[i], out[j]] = [out[j], out[i]];
      return out;
    });
  }

  async function save() {
    if (!title.trim()) { setError(t("El título es obligatorio.", "Title is required.")); return; }
    if (fields.length === 0) { setError(t("Agrega al menos un campo.", "Add at least one field.")); return; }
    setBusy(true);
    setError("");
    try {
      const payload = {
        title, slug: slug || undefined,
        description: description || null,
        fields: fields.map(({ _localId, ...f }) => f),
        interestType: interestType || null,
        thankYouTitle: thankYouTitle || null,
        thankYouMessage: thankYouMessage || null,
        redirectUrl: redirectUrl || null,
        brandColor: brandColor || null,
        isActive,
      };
      if (initial?.id) {
        await updateForm(initial.id, payload);
      } else {
        await createForm(payload);
      }
      await onSaved();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: "1.5rem", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem", flexWrap: "wrap", gap: ".5rem" }}>
        <div>
          <button onClick={onCancel} style={{ background: "none", border: "none", color: "var(--c-text2,#7aa3c0)", cursor: "pointer", fontSize: ".82rem", marginBottom: 6 }}>
            ← {t("Volver", "Back")}
          </button>
          <h2 style={{ margin: 0, fontSize: "1.4rem", color: "var(--c-white,#eef6ff)" }}>
            {initial?.id ? t("Editar formulario", "Edit form") : t("Nuevo formulario", "New form")}
          </h2>
        </div>
        <div style={{ display: "flex", gap: ".5rem" }}>
          <button onClick={() => setPreviewMode((p) => !p)} style={{
            padding: ".55rem 1rem", borderRadius: 9,
            background: previewMode ? "#00d4ff" : "var(--c-surface2,#112538)",
            color: previewMode ? "#0a1628" : "var(--c-text2,#7aa3c0)",
            border: "1px solid var(--c-border,#1a3550)",
            cursor: "pointer", fontWeight: 700, fontSize: ".82rem",
            display: "inline-flex", alignItems: "center", gap: 5,
          }}>
            <Eye size={13} /> {previewMode ? t("Editar", "Edit") : t("Vista previa", "Preview")}
          </button>
          <button onClick={save} disabled={busy} style={{
            padding: ".55rem 1.25rem", borderRadius: 9,
            background: "linear-gradient(135deg, #00d4ff, #00a8c8)", color: "#0a1628",
            border: "none", cursor: "pointer", fontWeight: 700, fontSize: ".82rem",
            opacity: busy ? .6 : 1,
          }}>
            {busy ? t("Guardando…", "Saving…") : (initial?.id ? t("Guardar cambios", "Save changes") : t("Crear formulario", "Create form"))}
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: "#3a1a1a", border: "1px solid #ef4444", borderRadius: 10,
          padding: ".75rem 1rem", marginBottom: 12, color: "#fca5a5",
          display: "flex", alignItems: "center", gap: 10, fontSize: ".88rem",
        }}>
          <AlertCircle size={16} /><span>{error}</span>
        </div>
      )}

      {previewMode ? (
        <PreviewPanel
          title={title} description={description}
          fields={fields} brandColor={brandColor}
          thankYouTitle={thankYouTitle} thankYouMessage={thankYouMessage}
          lang={lang}
        />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: "1rem", alignItems: "start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {/* Basic info */}
            <Card title={t("Información básica", "Basic info")}>
              <Field label={t("Título del formulario", "Form title")} required>
                <input value={title} onChange={(e) => setTitle(e.target.value)}
                       placeholder={t("ej. Cotiza tu seguro de salud", "e.g. Quote your health insurance")}
                       style={inputStyle()} />
              </Field>
              <Field label={t("Descripción", "Description")}>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                          placeholder={t("Aparece arriba del formulario público", "Shown above the public form")}
                          rows={3} style={{ ...inputStyle(), resize: "vertical" }} />
              </Field>
              <Field label={t("URL pública", "Public URL")}>
                <div style={{ display: "flex", alignItems: "center", gap: 4, color: "var(--c-text2,#7aa3c0)", fontSize: ".82rem" }}>
                  <span style={{ fontFamily: "var(--font-mono,monospace)" }}>/form/</span>
                  <input value={slug}
                         onChange={(e) => { setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")); setSlugTouched(true); }}
                         placeholder="my-form" style={{ ...inputStyle(), fontFamily: "var(--font-mono,monospace)" }} />
                </div>
              </Field>
              <Field label={t("Tipo de interés (auto-tag al lead)", "Interest type (auto-tag the lead)")}>
                <select value={interestType} onChange={(e) => setInterestType(e.target.value)} style={inputStyle()}>
                  <option value="">{t("— Sin auto-tag —", "— No auto-tag —")}</option>
                  {POLICY_INTEREST.map((p) => (<option key={p} value={p}>{p}</option>))}
                </select>
              </Field>
            </Card>

            {/* Fields */}
            <Card title={t(`Campos del formulario (${fields.length})`, `Form fields (${fields.length})`)}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {fields.map((f, i) => (
                  <FieldRow key={f._localId} f={f} idx={i} total={fields.length}
                            onUpdate={(p) => updateField(f._localId, p)}
                            onRemove={() => removeField(f._localId)}
                            onMove={(dir) => moveField(f._localId, dir)}
                            lang={lang} />
                ))}
              </div>
              <div style={{ marginTop: ".75rem", display: "flex", gap: 6, flexWrap: "wrap" }}>
                {FIELD_TYPES.map((tp) => (
                  <button key={tp.value} onClick={() => addField(tp.value)} style={{
                    padding: ".4rem .75rem", borderRadius: 8,
                    background: "var(--c-surface2,#112538)", color: "var(--c-text2,#7aa3c0)",
                    border: "1px solid var(--c-border,#1a3550)", cursor: "pointer",
                    fontSize: ".75rem", display: "inline-flex", alignItems: "center", gap: 4,
                  }}>
                    <Plus size={11} /> {tp.label}
                  </button>
                ))}
              </div>
            </Card>

            {/* Thank-you */}
            <Card title={t("Pantalla de agradecimiento", "Thank-you screen")}>
              <Field label={t("Título", "Title")}>
                <input value={thankYouTitle} onChange={(e) => setThankYouTitle(e.target.value)}
                       placeholder={t("¡Gracias!", "Thank you!")} style={inputStyle()} />
              </Field>
              <Field label={t("Mensaje", "Message")}>
                <textarea value={thankYouMessage} onChange={(e) => setThankYouMessage(e.target.value)}
                          placeholder={t("Te contactaremos pronto…", "We'll be in touch soon…")}
                          rows={3} style={{ ...inputStyle(), resize: "vertical" }} />
              </Field>
              <Field label={t("URL de redirección (opcional)", "Redirect URL (optional)")}>
                <input value={redirectUrl} onChange={(e) => setRedirectUrl(e.target.value)}
                       placeholder="https://tu-sitio.com/gracias" style={inputStyle()} />
                <div style={{ fontSize: ".72rem", color: "var(--c-text3,#45688a)", marginTop: 4 }}>
                  {t("Si pones URL, se redirige en vez de mostrar la pantalla de gracias.",
                     "If set, redirects instead of showing the thank-you screen.")}
                </div>
              </Field>
            </Card>
          </div>

          {/* Sidebar */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Card title={t("Estilo", "Branding")}>
              <Field label={t("Color principal", "Primary color")}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="color" value={brandColor || "#00d4ff"}
                         onChange={(e) => setBrandColor(e.target.value)}
                         style={{ width: 40, height: 40, border: "none", borderRadius: 8, cursor: "pointer", background: "transparent" }} />
                  <input value={brandColor} onChange={(e) => setBrandColor(e.target.value)} style={{ ...inputStyle(), fontFamily: "var(--font-mono,monospace)" }} />
                </div>
              </Field>
            </Card>

            <Card title={t("Estado", "Status")}>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} style={{ width: 18, height: 18, cursor: "pointer" }} />
                <div>
                  <div style={{ fontSize: ".88rem", color: "var(--c-white,#eef6ff)", fontWeight: 600 }}>
                    {t("Activo", "Active")}
                  </div>
                  <div style={{ fontSize: ".75rem", color: "var(--c-text2,#7aa3c0)" }}>
                    {t("Si lo desactivas, el link público devuelve error 410.",
                       "If off, the public link returns 410.")}
                  </div>
                </div>
              </label>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{
      background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)",
      borderRadius: 12, padding: "1rem 1.1rem",
    }}>
      <div style={{
        fontSize: ".72rem", letterSpacing: ".06em", textTransform: "uppercase",
        color: "var(--c-text3,#45688a)", fontWeight: 700, marginBottom: ".75rem",
      }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>{children}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: ".75rem", color: "var(--c-text2,#7aa3c0)", marginBottom: 4, fontWeight: 600 }}>
        {label}{required ? <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span> : null}
      </div>
      {children}
    </label>
  );
}

function inputStyle() {
  return {
    width: "100%", padding: ".55rem .75rem", borderRadius: 8,
    background: "var(--c-surface2,#112538)", color: "var(--c-white,#eef6ff)",
    border: "1px solid var(--c-border,#1a3550)", fontSize: ".88rem",
    outline: "none",
  };
}

function FieldRow({ f, idx, total, onUpdate, onRemove, onMove, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [open, setOpen] = useState(false);
  const needsOptions = f.type === "select" || f.type === "radio" || f.type === "checkbox";
  // Las opciones se escriben en un texto propio y NO se rehacen desde la lista
  // en cada tecla (Marilyn Sequera, 2026-10-02: "si agrego varias dejando
  // espacio o usando un guion, solo se ve la primera palabra"). Antes el valor
  // era `options.join("\n")` y cada cambio guardaba la lista ya limpia (sin
  // lineas vacias, sin espacios al final): el Enter y el espacio recien
  // escritos se borraban al instante, asi que no habia forma de pasar a la
  // segunda opcion. Mismo arreglo que ya tenia SheetsView. La lista se sigue
  // guardando limpia en cada tecla; solo el texto en pantalla queda como se
  // escribio. Si el campo cambia de id (otro campo en la misma fila), se
  // vuelve a leer de la lista.
  const [optsText, setOptsText] = useState(() => (f.options || []).join("\n"));
  useEffect(() => { setOptsText((f.options || []).join("\n")); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [f.id]);
  return (
    <div style={{
      background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)",
      borderRadius: 10, padding: ".7rem .85rem",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, color: "var(--c-text3,#45688a)" }}>
          <button onClick={() => onMove(-1)} disabled={idx === 0} style={miniBtn()}>↑</button>
          <button onClick={() => onMove(1)} disabled={idx === total - 1} style={miniBtn()}>↓</button>
        </div>
        <input value={f.label} onChange={(e) => onUpdate({ label: e.target.value })}
               placeholder={t("Etiqueta del campo", "Field label")}
               style={{ ...inputStyle(), flex: 1, padding: ".4rem .6rem", fontSize: ".82rem" }} />
        <select value={f.type} onChange={(e) => onUpdate({ type: e.target.value })}
                style={{ ...inputStyle(), width: 130, padding: ".4rem .5rem", fontSize: ".78rem" }}>
          {FIELD_TYPES.map((tp) => (<option key={tp.value} value={tp.value}>{tp.label}</option>))}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 4, color: "var(--c-text2,#7aa3c0)", fontSize: ".75rem", cursor: "pointer" }}>
          <input type="checkbox" checked={!!f.required} onChange={(e) => onUpdate({ required: e.target.checked })} />
          {t("Obligatorio", "Required")}
        </label>
        <button onClick={() => setOpen((o) => !o)} style={{ ...miniBtn(), padding: 4 }} title={t("Más", "More")}>
          <ChevronDown size={14} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
        </button>
        <button onClick={onRemove} style={{ ...miniBtn(), color: "#fca5a5" }} title={t("Eliminar", "Remove")}>
          <Trash2 size={13} />
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--c-border,#1a3550)", display: "flex", flexDirection: "column", gap: 8 }}>
          <Field label={t("ID del campo (interno)", "Field id (internal)")}>
            <input value={f.id} onChange={(e) => onUpdate({ id: e.target.value.replace(/[^a-zA-Z0-9_]/g, "") })}
                   placeholder="myField" style={{ ...inputStyle(), fontFamily: "var(--font-mono,monospace)" }} />
          </Field>
          <Field label={t("Placeholder (texto guía)", "Placeholder")}>
            <input value={f.placeholder || ""} onChange={(e) => onUpdate({ placeholder: e.target.value })} style={inputStyle()} />
          </Field>
          {needsOptions && (
            <Field label={t("Opciones (una por línea)", "Options (one per line)")}>
              <textarea value={optsText} onChange={(e) => { setOptsText(e.target.value); onUpdate({ options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) }); }}
                        rows={4} style={{ ...inputStyle(), resize: "vertical" }} />
            </Field>
          )}
          <Field label={t("Texto de ayuda", "Help text")}>
            <input value={f.helpText || ""} onChange={(e) => onUpdate({ helpText: e.target.value })} style={inputStyle()} />
          </Field>
        </div>
      )}
    </div>
  );
}

function miniBtn() {
  return {
    background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)",
    color: "var(--c-text2,#7aa3c0)", padding: 3, borderRadius: 5, cursor: "pointer",
    fontSize: 11, lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center",
  };
}

// ----------------------------------------------------------------------
// PreviewPanel — uses the SAME render logic as PublicFormPage so the
// preview is byte-accurate with what visitors will see.
// ----------------------------------------------------------------------

function PreviewPanel({ title, description, fields, brandColor, lang }) {
  return (
    <div style={{
      background: "#f4f7fb", borderRadius: 16, padding: "2rem 1rem",
      maxWidth: 560, margin: "1rem auto",
    }}>
      <PublicFormRenderer
        form={{ title, description, fields, brandColor }}
        onSubmit={() => {}}
        previewMode
        lang={lang}
      />
    </div>
  );
}

// Reusable renderer (also used by PublicFormPage in MyEmailView.jsx-style file below).
export function PublicFormRenderer({ form, onSubmit, previewMode, busy, lang = "en" }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [values, setValues] = useState(() => {
    const init = {};
    (form.fields || []).forEach((f) => { init[f.id] = f.type === "checkbox" ? [] : ""; });
    return init;
  });
  const brand = form.brandColor || "#00d4ff";

  function setVal(id, v) { setValues((prev) => ({ ...prev, [id]: v })); }
  function handleSubmit(e) {
    e.preventDefault();
    if (previewMode) return;
    onSubmit(values);
  }

  return (
    <form onSubmit={handleSubmit} style={{
      background: "#fff", borderRadius: 14, padding: "2rem 1.5rem",
      boxShadow: "0 8px 24px rgba(0,0,0,.06)", color: "#0a1628",
      fontFamily: "system-ui,-apple-system,Segoe UI,sans-serif",
    }}>
      <h1 style={{ margin: "0 0 .5rem", fontSize: "1.5rem", color: "#0a1628" }}>{form.title || t("Tu título", "Your title")}</h1>
      {form.description && (
        <p style={{ margin: "0 0 1.25rem", color: "#5b7a9a", fontSize: ".9rem", lineHeight: 1.55 }}>
          {form.description}
        </p>
      )}
      {/* Honeypot — invisible to users, bots fill it */}
      <input type="text" name="_hp" tabIndex={-1} autoComplete="off"
             style={{ position: "absolute", left: -9999, width: 1, height: 1, opacity: 0 }}
             onChange={(e) => setVal("_hp", e.target.value)} />
      <div style={{ display: "flex", flexDirection: "column", gap: ".85rem" }}>
        {(form.fields || []).map((f) => (
          <FieldInput key={f.id} f={f} value={values[f.id]} onChange={(v) => setVal(f.id, v)} brand={brand} />
        ))}
      </div>
      <button type="submit" disabled={busy || previewMode} style={{
        marginTop: "1.25rem", width: "100%", padding: "0.85rem", borderRadius: 10,
        background: brand, color: "#0a1628",
        border: "none", cursor: previewMode ? "not-allowed" : "pointer",
        fontWeight: 800, fontSize: "1rem",
      }}>
        {busy ? "…" : (previewMode ? t("(Vista previa — botón desactivado)", "(Preview — button disabled)") : t("Enviar", "Submit"))}
      </button>
    </form>
  );
}

function FieldInput({ f, value, onChange, brand }) {
  const baseStyle = {
    width: "100%", padding: ".7rem .9rem", borderRadius: 9,
    background: "#fff", color: "#0a1628",
    border: "1px solid #d1dbe6", fontSize: ".95rem", outline: "none",
    boxSizing: "border-box",
  };
  const labelEl = (
    <div style={{ fontSize: ".8rem", fontWeight: 700, color: "#3a5a7a", marginBottom: 4 }}>
      {f.label}{f.required ? <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span> : null}
    </div>
  );
  const helpEl = f.helpText
    ? <div style={{ fontSize: ".72rem", color: "#7d97b3", marginTop: 4 }}>{f.helpText}</div>
    : null;

  if (f.type === "textarea") {
    return (
      <div>{labelEl}
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)}
                  required={!!f.required} placeholder={f.placeholder || ""}
                  rows={4} style={{ ...baseStyle, resize: "vertical" }} />
        {helpEl}
      </div>
    );
  }
  if (f.type === "select") {
    return (
      <div>{labelEl}
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} required={!!f.required} style={baseStyle}>
          <option value="">{f.placeholder || "—"}</option>
          {(f.options || []).map((o) => (<option key={o} value={o}>{o}</option>))}
        </select>
        {helpEl}
      </div>
    );
  }
  if (f.type === "radio") {
    return (
      <div>{labelEl}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {(f.options || []).map((o) => (
            <label key={o} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: ".88rem" }}>
              <input type="radio" name={f.id} value={o} checked={value === o} onChange={(e) => onChange(e.target.value)} required={!!f.required} style={{ accentColor: brand }} />
              {o}
            </label>
          ))}
        </div>
        {helpEl}
      </div>
    );
  }
  if (f.type === "checkbox") {
    const arr = Array.isArray(value) ? value : [];
    function toggle(o) {
      if (arr.includes(o)) onChange(arr.filter((x) => x !== o));
      else onChange([...arr, o]);
    }
    return (
      <div>{labelEl}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {(f.options || []).map((o) => (
            <label key={o} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: ".88rem" }}>
              <input type="checkbox" checked={arr.includes(o)} onChange={() => toggle(o)} style={{ accentColor: brand }} />
              {o}
            </label>
          ))}
        </div>
        {helpEl}
      </div>
    );
  }
  // text / email / phone / number / date
  const htmlType = f.type === "phone" ? "tel" : f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "email" ? "email" : "text";
  return (
    <div>{labelEl}
      <input type={htmlType} value={value || ""} onChange={(e) => onChange(e.target.value)}
             required={!!f.required} placeholder={f.placeholder || ""} style={baseStyle} />
      {helpEl}
    </div>
  );
}

// ----------------------------------------------------------------------
// FormDetail — show form info, share link, QR, recent submissions
// ----------------------------------------------------------------------

function FormDetail({ formId, onBack, onEdit, onDeleted, lang }) {
  const es = lang === "es";
  const t = (esTxt, enTxt) => (es ? esTxt : enTxt);
  const [form, setForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError("");
      try {
        const f = await getForm(formId);
        if (cancelled) return;
        setForm(f);
        const s = await listFormSubmissions(formId);
        if (cancelled) return;
        setSubmissions(s);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [formId]);

  if (loading) return <div style={{ padding: "1.5rem", color: "var(--c-text2,#7aa3c0)" }}>{t("Cargando…", "Loading…")}</div>;
  if (error) return <div style={{ padding: "1.5rem", color: "#fca5a5" }}>{error}</div>;
  if (!form) return null;

  const url = publicUrlFor(form.slug);
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(url)}`;

  return (
    <div style={{ padding: "1.5rem", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: "1.25rem" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "var(--c-text2,#7aa3c0)", cursor: "pointer", fontSize: ".82rem", marginBottom: 6 }}>
          ← {t("Volver", "Back")}
        </button>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: ".5rem" }}>
          <h2 style={{ margin: 0, fontSize: "1.4rem", color: "var(--c-white,#eef6ff)" }}>{form.title}</h2>
          <div style={{ display: "flex", gap: ".5rem" }}>
            <button onClick={() => onEdit(form)} style={btn("primary")}>
              <Edit2 size={12} /> {t("Editar", "Edit")}
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: "1rem", alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Card title={t("Compartir", "Share")}>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input readOnly value={url} style={{ ...inputStyle(), fontFamily: "var(--font-mono,monospace)", fontSize: ".82rem" }} />
              <button onClick={() => { copyText(url).then((ok) => { if (ok) toast.success(t("¡Copiado!", "Copied!")); else toast.error(t("No se pudo copiar", "Couldn't copy")); }); }} style={btn("primary")}>
                <Copy size={12} /> {t("Copiar", "Copy")}
              </button>
              <button onClick={() => window.open(url, "_blank", "noopener,noreferrer")} style={btn("ghost")} title={t("Abrir", "Open")}>
                <ExternalLink size={12} />
              </button>
              <button onClick={() => setShowQR((q) => !q)} style={btn("ghost")} title="QR">
                <QrCode size={12} />
              </button>
            </div>
            {showQR && (
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <img src={qrUrl} alt="QR" style={{ borderRadius: 12, background: "#fff", padding: 8 }} />
                <div style={{ fontSize: ".72rem", color: "var(--c-text3,#45688a)", marginTop: 6 }}>
                  {t("Escanea con la cámara para abrir el formulario.",
                     "Scan with your camera to open the form.")}
                </div>
              </div>
            )}
          </Card>

          <Card title={t(`Submissions (${submissions.length})`, `Submissions (${submissions.length})`)}>
            {submissions.length === 0 ? (
              <div style={{ color: "var(--c-text2,#7aa3c0)", fontSize: ".88rem" }}>
                {t("Aún no hay envíos. Cuando alguien llene el form, aparece aquí y se crea un Lead automáticamente.",
                   "No submissions yet. When someone fills the form, it shows here and a Lead is auto-created.")}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {submissions.map((s) => (
                  <SubmissionRow key={s.id} sub={s} fields={form.fields} lang={lang} />
                ))}
              </div>
            )}
          </Card>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Card title={t("Estadísticas", "Stats")}>
            <Stat label={t("Vistas", "Views")} value={form.viewCount} />
            <Stat label={t("Envíos", "Submissions")} value={form._count?.submissions ?? form.submissionCount ?? 0} />
            <Stat label={t("Tasa conversión", "Conversion rate")} value={
              form.viewCount > 0
                ? `${(((form._count?.submissions ?? form.submissionCount ?? 0) / form.viewCount) * 100).toFixed(1)}%`
                : "—"
            } />
          </Card>
          <Card title={t("Estado", "Status")}>
            <div style={{ fontSize: ".88rem", color: form.isActive ? "#10b981" : "#94a3b8", fontWeight: 700 }}>
              {form.isActive ? `✅ ${t("Activo", "Active")}` : `⏸️ ${t("Pausado", "Paused")}`}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px dashed var(--c-border,#1a3550)" }}>
      <div style={{ color: "var(--c-text2,#7aa3c0)", fontSize: ".82rem" }}>{label}</div>
      <div style={{ color: "var(--c-white,#eef6ff)", fontWeight: 700, fontSize: ".88rem" }}>{value}</div>
    </div>
  );
}

function SubmissionRow({ sub, fields, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [open, setOpen] = useState(false);
  const data = sub.data || {};
  const fieldLabel = (id) => fields?.find((f) => f.id === id)?.label || id;
  return (
    <div style={{
      background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)",
      borderRadius: 10, padding: ".7rem .85rem",
    }}>
      <div onClick={() => setOpen((o) => !o)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: "var(--c-white,#eef6ff)", fontSize: ".88rem" }}>
            {data.firstName || data.first_name || data.name || "—"} {data.lastName || data.last_name || ""}
          </div>
          <div style={{ fontSize: ".75rem", color: "var(--c-text2,#7aa3c0)" }}>
            {data.email || ""} {data.phone ? `· ${data.phone}` : ""}
          </div>
        </div>
        <div style={{ fontSize: ".72rem", color: "var(--c-text3,#45688a)" }}>
          {new Date(sub.createdAt).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" })}
        </div>
        <ChevronDown size={14} color="#7aa3c0" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </div>
      {open && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--c-border,#1a3550)", display: "flex", flexDirection: "column", gap: 4 }}>
          {Object.entries(data).map(([k, v]) => (
            <div key={k} style={{ fontSize: ".78rem", color: "var(--c-text2,#7aa3c0)" }}>
              <strong style={{ color: "var(--c-text,#a8c5e0)" }}>{fieldLabel(k)}:</strong>{" "}
              {Array.isArray(v) ? v.join(", ") : String(v ?? "")}
            </div>
          ))}
          {sub.leadId && (
            <div style={{ fontSize: ".72rem", color: "#10b981", marginTop: 4 }}>
              ✅ {t("Lead creado:", "Lead created:")} {sub.leadId}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
